import type { ActivityOf } from '@wyn/content';
import { BTN, type HorsemanshipSim, horsemanshipSim, THROWN } from '@wyn/game-core';
import { Progress, Stage, Track } from './parts.tsx';
import type { LiveGameProps } from './registry.tsx';
import { useRealtime } from './useRealtime.ts';

const KEYS = { ArrowLeft: BTN.left, ArrowRight: BTN.right, KeyA: BTN.left, KeyD: BTN.right };

/** The Horse Fair: lean against each buck until the horse calms. */
export function HorsemanshipGame({ session, activity, onDone }: LiveGameProps) {
  const a = activity as ActivityOf<'horsemanship'>;
  const rt = useRealtime<HorsemanshipSim>({
    create: () => horsemanshipSim(a, session.level, session.seed!),
    onDone: (_sim, trace) => onDone(trace),
    keys: KEYS,
  });
  const sim = rt.sim;
  if (!sim) return null;
  const needle = Math.max(0, Math.min(100, 50 + (sim.lean / THROWN) * 50));
  const lean = (bit: number, label: string) => (
    <button
      type="button"
      {...rt.holdProps(bit)}
      disabled={!!sim.result}
      className={`flex-1 select-none touch-none border-2 py-4 text-lg transition-colors ${rt.isHeld(bit) ? 'border-gilt bg-gilt text-vellum' : 'border-gilt/70 text-gilt'}`}
    >
      {label}
    </button>
  );
  return (
    <Stage fx={rt.fx}>
      <p className="text-sm text-ink-dim">
        Hold <b>◀</b> or <b>▶</b> (or the arrow keys) to lean against the horse’s lurches. Keep the needle near the centre until the calm bar fills. Past either edge, you are thrown. Watch the horse: it gathers itself a beat before each buck, and the arrow shows which way it will throw you.
      </p>
      <div className="relative flex h-40 items-end justify-center overflow-hidden border border-rule bg-gradient-to-b from-[#3a3a30] to-[#2a2a22]">
        <div
          className={`absolute top-2 font-display text-3xl text-gilt transition-opacity duration-75 ${sim.tell ? 'opacity-100' : 'opacity-0'} ${sim.tell < 0 ? 'left-4' : 'right-4'}`}
          role="status"
          aria-label={sim.tell ? `The horse is about to throw you ${sim.tell < 0 ? 'left' : 'right'}` : undefined}
        >
          {sim.tell < 0 ? '⟸' : '⟹'}
        </div>
        <div className="relative origin-bottom text-[84px] leading-none" style={{ transform: `rotate(${sim.buckAngle + sim.tell * 3}deg) translateY(${sim.tell ? 4 : 0}px)` }} aria-hidden>
          🐎
          <div className="absolute bottom-[62%] left-[34%] origin-bottom text-[40px]" style={{ transform: `rotate(${sim.lean * 0.5}deg)` }}>🤠</div>
        </div>
      </div>
      <Track value={needle} label="Balance" band={[35, 65]} hatch={[[0, 6], [94, 100]]} reading={{ now: sim.lean, min: -THROWN, max: THROWN }} />
      <Progress value={Math.min(1, sim.time / sim.duration)} label="Calming" />
      <div className="flex gap-2">
        {lean(BTN.left, '◀ Lean')}
        {lean(BTN.right, 'Lean ▶')}
      </div>
    </Stage>
  );
}
